import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Ic } from './icons';
import FreeElement from '../components/EditorCanvas/FreeElement';
import { useDroppable } from '@dnd-kit/core';

/* ─────────────────────────────────────────────────────────────
   ERPForce PDF Builder — Stationery Master · document render layer
   The stationery master = ONE global header + ONE global footer +
   watermark that EVERY document template inherits. The body of a
   document (doc-title bar, parties, items, totals…) is supplied by
   the per-type template; the stationery wraps it on every page.
   Exposes export const STDoc = { PaperSheet, DOC_BODIES, DOC_META }
   ───────────────────────────────────────────────────────────── */



// ══ shared sample data (used only to preview the master) ══════
const ITEMS = [
  { sku: 'EQ-7741', name: 'Industrial network switch, 48-port managed', detail: 'Layer-3, PoE+', qty: 12, unit: 'pcs', price: 2150, amount: 25800 },
  { sku: 'EQ-3320', name: 'Rack-mount UPS 3kVA online', detail: 'Extended battery module', qty: 6, unit: 'pcs', price: 4380, amount: 24966 },
  { sku: 'CB-1102', name: 'Cat6A S/FTP cabling, 305m drum', detail: 'LSZH, blue', qty: 20, unit: 'drum', price: 690, amount: 13800 },
  { sku: 'SV-0099', name: 'On-site installation & commissioning', detail: 'Certified engineers, 3 days', qty: 1, unit: 'lot', price: 8500, amount: 8500 },
];
const CONT_ITEMS = [
  { sku: 'EQ-2280', name: 'Fibre patch panel 24-port LC duplex', detail: 'OS2 single-mode', qty: 8, unit: 'pcs', price: 540, amount: 4320 },
  { sku: 'AC-4410', name: 'Precision cooling unit, in-row 12kW', detail: 'Chilled-water, EC fans', qty: 2, unit: 'pcs', price: 18900, amount: 34020 },
  { sku: 'CB-2204', name: 'Power distribution unit, metered 32A', detail: 'Vertical, 24 outlets', qty: 8, unit: 'pcs', price: 1240, amount: 9920 },
  { sku: 'SV-0102', name: 'Annual maintenance contract (Tier-1)', detail: '24×7 response, 12 months', qty: 1, unit: 'yr', price: 22000, amount: 22000 },
];
const money = (n) => 'AED ' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── inline-editable text (WYSIWYG → model). Commits on blur so the
//    caret never jumps mid-typing. Mirrors the PDF Builder edit lens.
function Editable({ tag = 'div', value, onCommit, edit, className = '', dir, placeholder, title, style }) {
  const Tag = tag as any;
  const ref = useRef<any>(null);

  useEffect(() => {
    if (ref.current && ref.current.textContent !== (value || '')) {
      ref.current.textContent = value || '';
    }
  }, [value]);

  if (!edit) {
    return <Tag className={className} dir={dir} style={style}>{value || placeholder || ''}</Tag>;
  }

  return (
    <Tag
      ref={ref}
      className={`${className} st-ed`} dir={dir} style={{ minWidth: 20, minHeight: 16, outline: 'none', cursor: 'inherit', ...style }} contentEditable suppressContentEditableWarning
      spellCheck={false} title={title || 'Double-click to edit'} data-empty={!value} data-ph={placeholder || ''}
      onBlur={(e: any) => { const t = e.currentTarget.textContent.replace(/\s+/g, ' ').trim(); if (t !== (value || '')) onCommit(t); }}
      onKeyDown={(e: any) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } if (e.key === 'Escape') { e.currentTarget.textContent = value || ''; e.currentTarget.blur(); } }}
    />
  );
}

const CUSTOMER = {
  name: 'Etisalat Digital — Smart Solutions', attn: 'Procurement Dept.',
  line1: 'Etisalat Tower 2, Sheikh Zayed Road', line2: 'Dubai, United Arab Emirates', trn: '100200300400500',
};

// per-doc metadata (accent is the DOCUMENT accent — independent of the stationery brand colour)
const DOC_META = {
  quotation: { name: 'Quotation', accent: '#1A8A56', no: 'QT-2026-0418', date: '30 May 2026', tag: 'Valid to 29 Jun 2026', tagClass: 'pbd-status--draft' },
  invoice: { name: 'Tax Invoice', accent: '#1B2C6E', no: 'INV-2026-1183', date: '30 May 2026', tag: 'Payment due 29 Jun 2026', tagClass: 'pbd-status--due' },
  delivery: { name: 'Delivery Note', accent: '#8a540b', no: 'DN-2026-0902', date: '30 May 2026', tag: 'Inv ref INV-2026-1183', tagClass: '' },
};

// ── doc-title bar (the body's masthead — NOT the letterhead) ──
function DocTitle({ t }) {
  return (
    <div className="st-doctitle">
      <div className="st-doctitle-l">
        <div className="st-doctype">{t.name}</div>
        <div className="st-doctype-rule" />
      </div>
      <div className="st-doctitle-r">
        <div className="pbd-ref">No. <b>{t.no}</b></div>
        <div className="pbd-ref">{t.date}</div>
        {t.tag && <span className={`pbd-status ${t.tagClass}`}>{t.tag}</span>}
      </div>
    </div>
  );
}

function Parties({ type }) {
  return (
    <div className={`pbd-parties ${type === 'delivery' ? '' : 'pbd-parties--2'}`}>
      <div className="pbd-party">
        <div className="pbd-party-h">{type === 'invoice' ? 'Bill to' : type === 'delivery' ? 'Ship to' : 'Quotation for'}</div>
        <div className="pbd-party-name">{CUSTOMER.name}</div>
        <div className="pbd-party-line">Attn: {CUSTOMER.attn}</div>
        <div className="pbd-party-line">{CUSTOMER.line1}</div>
        <div className="pbd-party-line">{CUSTOMER.line2}</div>
        <div className="pbd-party-line">TRN {CUSTOMER.trn}</div>
      </div>
      <div className="pbd-party">
        <div className="pbd-party-h">{type === 'delivery' ? 'Despatch' : 'Reference'}</div>
        {type === 'delivery' ? (
          <>
            <div className="pbd-party-line">Vehicle: Mercedes Actros · D-44192</div>
            <div className="pbd-party-line">Driver: M. Iqbal</div>
            <div className="pbd-party-line">PO: PO-ETD-55021</div>
          </>
        ) : (
          <>
            <div className="pbd-party-line">PO number: PO-ETD-55021</div>
            <div className="pbd-party-line">Sales rep: L. Haddad</div>
            <div className="pbd-party-line">Currency: AED</div>
          </>
        )}
      </div>
      {type === 'delivery' && (
        <div className="pbd-party">
          <div className="pbd-party-h">Customer</div>
          <div className="pbd-party-name">Etisalat Digital</div>
          <div className="pbd-party-line">Dubai Investment Park 2</div>
          <div className="pbd-party-line">Dubai, UAE</div>
        </div>
      )}
    </div>
  );
}

function ItemsTable({ type, rows, cont }) {
  const priced = type !== 'delivery';
  return (
    <div className="pbd-items-wrap">
      {cont && <div className="pbd-items-cont" style={{ textAlign: 'left', paddingBottom: 4 }}>… continued from previous page</div>}
      <table className="pbd-items">
        <thead>
          {priced ? (
            <tr><th className="l">#</th><th className="l">Description</th><th>Qty</th><th>Unit</th><th>Unit price</th><th>Amount</th></tr>
          ) : (
            <tr><th className="l">#</th><th className="l">Code</th><th className="l">Description</th><th>Unit</th><th>Qty ord.</th><th>Qty del.</th></tr>
          )}
        </thead>
        <tbody>
          {rows.map((it, i) => priced ? (
            <tr key={i}>
              <td className="l it-no">{i + 1}</td>
              <td className="l it-desc"><b>{it.name}</b><span className="it-sku">{it.sku} · {it.detail}</span></td>
              <td>{it.qty}</td><td>{it.unit}</td><td>{money(it.price)}</td><td><b>{money(it.amount)}</b></td>
            </tr>
          ) : (
            <tr key={i}>
              <td className="l it-no">{i + 1}</td>
              <td className="l"><span className="it-sku" style={{ fontSize: 9 }}>{it.sku}</span></td>
              <td className="l it-desc"><b>{it.name}</b><span className="it-sku">{it.detail}</span></td>
              <td>{it.unit}</td><td>{it.qty}</td><td><b>{it.qty}</b></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Totals() {
  return (
    <div className="pbd-totals-wrap">
      <div className="pbd-totals">
        <div className="pbd-tot-row"><span>Subtotal</span><span className="pbd-tot-v">{money(73066)}</span></div>
        <div className="pbd-tot-row pbd-tot-row--sub"><span>VAT (5%)</span><span className="pbd-tot-v">{money(3653.3)}</span></div>
        <div className="pbd-tot-grand"><span className="pbd-tot-k">Grand total</span><span className="pbd-tot-v">{money(76719.3)}</span></div>
      </div>
    </div>
  );
}

function Notes({ type }) {
  if (type === 'delivery') {
    return (
      <div className="pbd-sign" style={{ marginTop: 30 }}>
        <div className="pbd-sign-line"><div className="pbd-sign-rule"><b>Received by (name &amp; signature)</b>Date &amp; company stamp</div></div>
        <div className="pbd-sign-line"><div className="pbd-sign-rule"><b>M. Iqbal</b>Delivered by</div></div>
      </div>
    );
  }
  return (
    <>
      <div className="pbd-cols">
        <div>
          <div className="pbd-notes-h">Notes</div>
          <div className="pbd-notes-body">Thank you for the opportunity to {type === 'invoice' ? 'serve you. Please quote the invoice number on all remittances.' : 'quote. We would be pleased to arrange a site survey at your convenience.'}</div>
        </div>
        <div>
          <div className="pbd-notes-h">{type === 'invoice' ? 'Payment terms · Net 30' : 'Terms & conditions'}</div>
          <div className="pbd-notes-body">
            <ol><li>Prices in AED, valid 30 days.</li><li>50% advance with PO, balance on delivery.</li></ol>
          </div>
        </div>
      </div>
      <div className="pbd-sign" style={{ marginTop: 28 }}>
        <div className="pbd-sign-line"><div className="pbd-sign-rule"><b>L. Haddad</b>Sales Manager</div></div>
        <div className="pbd-sign-line"><div className="pbd-sign-rule"><b>&nbsp;</b>Customer acceptance · date</div></div>
      </div>
    </>
  );
}

// unified body — the per-template content the stationery wraps.
// page 0 carries the masthead + parties; totals/notes/signature land on
// the LAST page so a 1-page doc is complete and a 2-page doc splits cleanly.
function Body({ type, pageIndex, total }) {
  const last = pageIndex === total - 1;
  return (
    <>
      {pageIndex === 0 && <><DocTitle t={DOC_META[type]} /><Parties type={type} /></>}
      <ItemsTable type={type} rows={pageIndex === 0 ? ITEMS : CONT_ITEMS} cont={pageIndex > 0} />
      {last && type !== 'delivery' && <Totals />}
      {last && <Notes type={type} />}
    </>
  );
}

// ══ STATIONERY MASTER — header ════════════════════════════════
function FreeWrap({ id, attr, defaultPos, edit, ed, bounds, children }: any) {
  const safeNum = (val: any, fallback: any) => {
    const parsed = parseFloat(val);
    return isNaN(parsed) ? fallback : parsed;
  };

  const x = safeNum(attr?.x, defaultPos.x);
  const y = safeNum(attr?.y, defaultPos.y);
  const w = safeNum(attr?.width, defaultPos.w);
  const h = safeNum(attr?.height, defaultPos.h);
  const r = safeNum(attr?.rotation, 0);

  const elemType = attr?.type || (id === 'logo' ? 'image' : id === 'rule' ? 'divider' : (id === 'regBand' || id === 'pageNumbers') ? 'buttons' : 'text');

  if (edit && ed) {
    return (
      <FreeElement
        element={{ id, attributes: attr || {}, x, y, width: w, height: h, rotation: r, type: elemType, content: attr?.content || '' } as any}
        isSelected={ed.selectedElement === id}
        canvasBounds={bounds || ed.canvasBounds}
        onSelect={() => ed.onSelectElement(id)}
        onMove={(moveId, nx, ny) => ed.onChangeElement(moveId, nx, ny, w, h, r)}
        onResize={(resId, nx, ny, nw, nh) => ed.onChangeElement(resId, nx, ny, nw, nh, r)}
        onRotate={(rotId, nr) => ed.onChangeElement(rotId, x, y, w, h, nr)}
        onDelete={ed.onDeleteElement}
        onUpdate={ed.onUpdateElement}
        onBringForward={() => { }}
        onSendBackward={() => { }}
      >
        {children}
      </FreeElement>
    );
  }
  return (
    <div style={{ position: 'absolute', left: x, top: y, width: w, height: h, transform: `rotate(${r}deg)` }}>
      {children || <div className="st-free-content" dangerouslySetInnerHTML={{ __html: attr?.content || '' }} style={{ width: '100%', height: '100%' }} />}
    </div>
  );
}

const getTextStyle = (attrs) => {
  if (!attrs || !attrs.styles) return {};
  const { fontFamily, fontSize, lineHeight, textAlign, fontWeight, fontStyle, textDecoration, color } = attrs.styles;

  const style: any = { fontFamily, fontSize, lineHeight, textAlign, fontWeight, fontStyle, textDecoration, color };

  // Map text-align to flexbox justify-content for containers that use flex layout
  if (textAlign) {
    if (textAlign === 'left') style.justifyContent = 'flex-start';
    if (textAlign === 'right') style.justifyContent = 'flex-end';
    if (textAlign === 'center') style.justifyContent = 'center';
  }

  // Filter out undefined values to let CSS defaults work if not specified
  return Object.fromEntries(
    Object.entries(style)
      .filter(([_, v]) => v !== undefined)
  );
};

function Header({ m, compact, ed }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'st-drop-header' });
  const h = m.header;
  const variant = compact ? 'compact' : h.variant;
  const edit = ed && ed.on;
  const logoSrc = h.logoSrc !== undefined && h.logoSrc !== null ? h.logoSrc : '';
  const logoH = 46;

  const attrs = h.logoAttributes || {};
  const styles = attrs.styles || {};

  const logoStyle: any = {
    width: '100%',
    height: '100%',
    opacity: styles.opacity !== undefined ? styles.opacity : 1,
    objectFit: styles.objectFit || 'contain',
  };
  if (styles.borderRadius !== undefined) logoStyle.borderRadius = styles.borderRadius + 'px';
  if (styles.is_bordered) {
    logoStyle.border = `${styles.border || 1}px ${styles.borderStyle || 'solid'} ${styles.borderColor || '#000000'}`;
    logoStyle.boxSizing = 'border-box';
  }

  const setH = ed ? ed.setH : () => { };

  const paper = m.paper || 'A4';
  const orient = m.orient || 'portrait';
  let [pageW, pageH] = paper === 'Letter' ? [816, 1056] : [794, 1123];
  if (orient === 'landscape') { const t = pageW; pageW = pageH; pageH = t; }
  const cw = pageW;

  // Base heights and defaults
  const headerHeight = compact ? 60 : (h.height || m.reserve?.top || 156);
  const bounds = { width: pageW, height: headerHeight };

  if (ed) {
    ed.canvasBounds = bounds;
  }

  let defaults;
  if (variant === 'centered') {
    defaults = {
      logo: { x: cw / 2 - logoH, y: 30, w: logoH * 2, h: logoH },
      nameEN: { x: cw / 2 - 100, y: 30 + logoH + 10, w: 200, h: 25 },
      nameAR: { x: cw / 2 - 100, y: 30 + logoH + 35, w: 200, h: 25 },
      tagline: { x: cw / 2 - 100, y: 30 + logoH + 60, w: 200, h: 20 },
      rule: { x: 0, y: Math.max(0, headerHeight - 2), w: pageW, h: 2 }
    };
  } else if (variant === 'split') {
    defaults = {
      logo: { x: 40, y: 30, w: logoH * 2, h: logoH },
      tagline: { x: 40, y: 30 + logoH + 10, w: 200, h: 20 },
      nameEN: { x: cw - 240, y: 30, w: 200, h: 25 },
      nameAR: { x: cw - 240, y: 55, w: 200, h: 25 },
      rule: { x: 0, y: Math.max(0, headerHeight - 2), w: pageW, h: 2 }
    };
  } else if (variant === 'compact') {
    defaults = {
      logo: { x: 40, y: 15, w: logoH * 2, h: logoH },
      nameEN: { x: 180, y: 25, w: 200, h: 25 },
      nameAR: { x: 390, y: 25, w: 200, h: 25 },
      tagline: { x: cw - 240, y: 25, w: 200, h: 20 },
      rule: { x: 0, y: Math.max(0, headerHeight - 2), w: pageW, h: 2 }
    };
  } else {
    // standard
    defaults = {
      logo: { x: 40, y: 30, w: logoH * 2, h: logoH },
      nameEN: { x: cw - 240, y: 30, w: 200, h: 25 },
      nameAR: { x: cw - 240, y: 55, w: 200, h: 25 },
      tagline: { x: cw - 240, y: 80, w: 200, h: 20 },
      rule: { x: 0, y: Math.max(0, headerHeight - 2), w: pageW, h: 2 }
    };
  }

  const isDraggingAny = Boolean(ed?.activeDragId);
  const isHighlighted = isOver || (edit && isDraggingAny);

  const effNameENAttr = {
    ...(h.nameENAttributes || {}),
    ...(h.nameENAttributes?.x === 554 ? { x: defaults.nameEN.x } : {})
  };
  const effNameARAttr = {
    ...(h.nameARAttributes || {}),
    ...(h.nameARAttributes?.x === 554 ? { x: defaults.nameAR.x } : {})
  };
  const effTaglineAttr = {
    ...(h.taglineAttributes || {}),
    ...(h.taglineAttributes?.x === 554 ? { x: defaults.tagline.x } : {})
  };

  return (
    <div
      ref={setNodeRef}
      className={`st-head st-head--${variant} ${edit ? 'is-edit' : ''}`}
      style={{
        position: 'relative',
        width: '100%',
        height: headerHeight,
        overflow: 'hidden',
        borderBottom: edit ? (isHighlighted ? '2px dashed #2563eb' : '1.5px dashed #cbd5e1') : 'none',
        background: isOver ? 'rgba(37,99,235,0.08)' : (edit && isDraggingAny ? 'rgba(37,99,235,0.02)' : 'transparent'),
        transition: 'background 0.15s, border-color 0.15s'
      }}
    >
      {edit && (
        <div style={{
          position: 'absolute',
          bottom: 3,
          right: 8,
          fontSize: 10,
          fontWeight: 500,
          color: isHighlighted ? '#2563eb' : '#94a3b8',
          background: isHighlighted ? 'rgba(37,99,235,0.1)' : 'rgba(241,245,249,0.85)',
          padding: '1px 6px',
          borderRadius: 4,
          pointerEvents: 'none',
          userSelect: 'none',
          zIndex: 1,
          border: '1px solid',
          borderColor: isHighlighted ? 'rgba(37,99,235,0.2)' : 'rgba(203,213,225,0.6)'
        }}>
          {isOver ? 'Drop in Header area' : `Header area · ${headerHeight}px`}
        </div>
      )}

      {h.showLogo && logoSrc && (
        <FreeWrap id="logo" attr={h.logoAttributes} defaultPos={defaults.logo} edit={edit} ed={ed} bounds={bounds}>
          <img className="st-logo" src={logoSrc} alt="" style={logoStyle} draggable={false} />
        </FreeWrap>
      )}

      {(() => {
        const defaultAlign = (variant === 'standard' || variant === 'split') ? 'right' : variant === 'centered' ? 'center' : 'left';
        return (
          <>
            {h.nameEN && (
              <FreeWrap id="nameEN" attr={effNameENAttr} defaultPos={defaults.nameEN} edit={edit} ed={ed} bounds={bounds}>
                <Editable
                  className="st-name-en"
                  value={h.nameEN}
                  edit={edit}
                  placeholder="Company name"
                  onCommit={(v) => setH({ nameEN: v })}
                  style={{
                    width: '100%',
                    textAlign: defaultAlign,
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#14171b',
                    lineHeight: 1.2,
                    ...getTextStyle(h.nameENAttributes)
                  }}
                />
              </FreeWrap>
            )}

            {h.bilingual && h.nameAR && (
              <FreeWrap id="nameAR" attr={effNameARAttr} defaultPos={defaults.nameAR} edit={edit} ed={ed} bounds={bounds}>
                <Editable
                  className="st-name-ar"
                  dir="rtl"
                  value={h.nameAR}
                  edit={edit}
                  placeholder="الاسم بالعربية"
                  onCommit={(v) => setH({ nameAR: v })}
                  style={{
                    width: '100%',
                    textAlign: defaultAlign,
                    fontFamily: "'Noto Kufi Arabic', 'Cairo', 'IBM Plex Sans Arabic', Tahoma, sans-serif",
                    fontSize: '15px',
                    fontWeight: 600,
                    color: '#3a3f46',
                    lineHeight: 1.4,
                    ...getTextStyle(h.nameARAttributes)
                  }}
                />
              </FreeWrap>
            )}

            {h.tagline && !compact && (
              <FreeWrap id="tagline" attr={effTaglineAttr} defaultPos={defaults.tagline} edit={edit} ed={ed} bounds={bounds}>
                <Editable
                  className="st-tagline"
                  value={h.tagline}
                  edit={edit}
                  placeholder="www.example.com"
                  onCommit={(v) => setH({ tagline: v })}
                  style={{
                    width: '100%',
                    textAlign: defaultAlign,
                    fontSize: '10px',
                    color: 'var(--st-brand, #2EB273)',
                    ...getTextStyle(h.taglineAttributes)
                  }}
                />
              </FreeWrap>
            )}
          </>
        );
      })()}

      {h.rule && (() => {
        const rawW = Number(h.ruleAttributes?.width);
        const isDefaultW = !rawW || rawW === 714 || rawW === 794 || (pageW > 794 && rawW <= 794);
        const ruleW = isDefaultW ? pageW : rawW;
        const ruleX = (isDefaultW || h.ruleAttributes?.x === 40 || !h.ruleAttributes?.x) ? 0 : h.ruleAttributes.x;
        const ruleY = h.ruleAttributes?.y !== undefined ? h.ruleAttributes.y : Math.max(0, headerHeight - 2);
        return (
          <FreeWrap
            id="rule"
            attr={{
              ...(h.ruleAttributes || {}),
              x: ruleX,
              y: ruleY,
              width: ruleW,
              w: ruleW
            }}
            defaultPos={defaults.rule}
            edit={edit}
            ed={ed}
            bounds={bounds}
          >
            <div
              className="st-brand-rule"
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: 'var(--st-brand)',
                borderRadius: 0,
                ...getTextStyle(h.ruleAttributes)
              }}
            />
          </FreeWrap>
        );
      })()}

      {(m.sections || []).filter((s: any) => s.zone === 'header').map((sec: any) => (
        <FreeWrap key={sec.id} id={sec.id} attr={{ ...sec.attributes, x: sec.x ?? sec.attributes?.x, y: sec.y ?? sec.attributes?.y, w: sec.width ?? sec.attributes?.w, h: sec.height ?? sec.attributes?.h, width: sec.width ?? sec.attributes?.width, height: sec.height ?? sec.attributes?.height, type: sec.type, content: sec.content }} defaultPos={{ x: 50, y: 50, w: 200, h: 50 }} edit={edit} ed={ed} bounds={bounds} />
      ))}

    </div>
  );
}

// ══ STATIONERY MASTER — footer ════════════════════════════════
function Footer({ m, pageIndex, total, compact, ed }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'st-drop-footer' });
  const f = m.footer;
  const edit = ed && ed.on;
  const setF = ed ? ed.setF : () => { };
  const setReg = ed ? ed.setReg : () => { };
  const fmt = (f.format || 'Page {n} of {total}').replace('{n}', pageIndex + 1).replace('{total}', total);
  const regList = edit ? f.regItems : f.regItems.filter(r => r.value);

  // Continuation pages get a slim single-line footer — the full reg band +
  // contact block only belongs on the first page.
  if (compact) {
    return (
      <div className="st-foot st-foot--compact">
        <div className="st-foot-meta">
          {f.note && <span>{f.note}</span>}
          {f.pageNumbers && <span className="st-foot-pg">{fmt}</span>}
        </div>
      </div>
    );
  }

  const paper = m.paper || 'A4';
  const orient = m.orient || 'portrait';
  let [pageW, pageH] = paper === 'Letter' ? [816, 1056] : [794, 1123];
  if (orient === 'landscape') { const t = pageW; pageW = pageH; pageH = t; }
  const cw = pageW;

  const footerHeight = compact ? 40 : (f.height || m.reserve?.bottom || 120);
  const bounds = { width: pageW, height: footerHeight };
  if (ed) ed.canvasBounds = bounds;

  const defaults = {
    regBand: { x: 0, y: 0, w: pageW, h: 25 },
    contact: { x: 40, y: Math.max(0, Math.min(40, footerHeight - 60)), w: cw - 80, h: 25 },
    note: { x: 40, y: Math.max(0, Math.min(70, footerHeight - 25)), w: 400, h: 20 },
    pageNumbers: { x: cw - 194, y: Math.max(0, Math.min(70, footerHeight - 25)), w: 154, h: 20 },
  };

  const contactStyle: any = getTextStyle(f.contactAttributes);
  // Icons always use the stationery brand colour, independent of text colour
  const contactIconStyle = { color: 'var(--st-brand)' };

  const isDraggingAny = Boolean(ed?.activeDragId);
  const isHighlighted = isOver || (edit && isDraggingAny);

  return (
    <div
      ref={setNodeRef}
      className={`st-foot ${edit ? 'is-edit' : ''}`}
      style={{
        position: 'relative',
        width: '100%',
        height: footerHeight,
        overflow: 'hidden',
        borderTop: edit ? (isHighlighted ? '2px dashed #2563eb' : '1.5px dashed #cbd5e1') : 'none',
        background: isOver ? 'rgba(37,99,235,0.08)' : (edit && isDraggingAny ? 'rgba(37,99,235,0.02)' : 'transparent'),
        transition: 'background 0.15s, border-color 0.15s'
      }}
    >
      {edit && (
        <div style={{
          position: 'absolute',
          top: 3,
          right: 8,
          fontSize: 10,
          fontWeight: 500,
          color: isHighlighted ? '#2563eb' : '#94a3b8',
          background: isHighlighted ? 'rgba(37,99,235,0.1)' : 'rgba(241,245,249,0.85)',
          padding: '1px 6px',
          borderRadius: 4,
          pointerEvents: 'none',
          userSelect: 'none',
          zIndex: 1,
          border: '1px solid',
          borderColor: isHighlighted ? 'rgba(37,99,235,0.2)' : 'rgba(203,213,225,0.6)'
        }}>
          {isOver ? 'Drop in Footer area' : `Footer area · ${footerHeight}px`}
        </div>
      )}

      {f.regBand && (
        <FreeWrap id="regBand" attr={{ ...(f.regBandAttributes || {}), x: f.regBandAttributes?.x ?? 0, width: pageW, w: pageW }} defaultPos={defaults.regBand} edit={edit} ed={ed} bounds={bounds}>
          <div className="st-foot-band" style={{ width: '100%', height: '100%', boxSizing: 'border-box', ...getTextStyle(f.regBandAttributes) }}>
            {regList.map((r, i) => (
              <span key={i}>
                {(r.label || edit) && <Editable tag="span" className="st-ed-inline" value={r.label} edit={edit} placeholder="Label" onCommit={(v) => setReg(i, { label: v })} />}
                {' '}
                {(r.value || edit) && <Editable tag="span" className="st-ed-inline" value={r.value} edit={edit} placeholder="number" onCommit={(v) => setReg(i, { value: v })} />}
              </span>
            ))}
          </div>
        </FreeWrap>
      )}

      {f.contactRow && (f.address || f.phone || f.email) && (
        <FreeWrap id="contact" attr={{ ...(f.contactAttributes || {}), width: (f.contactAttributes?.width === 714 || !f.contactAttributes?.width) ? (cw - 80) : f.contactAttributes.width }} defaultPos={defaults.contact} edit={edit} ed={ed} bounds={bounds}>
          <div className="st-foot-contact" style={{ width: '100%', height: '100%', boxSizing: 'border-box', ...contactStyle }}>
            {(f.address || edit) && <span><Ic k="pin" style={contactIconStyle} /><Editable tag="span" className="st-ed-inline" value={f.address} edit={edit} placeholder="Address" onCommit={(v) => setF({ address: v })} /></span>}
            {(f.phone || edit) && <span><Editable tag="span" className="st-ed-inline" value={f.phone} edit={edit} placeholder="Phone" onCommit={(v) => setF({ phone: v })} /></span>}
            {(f.email || edit) && <span><Ic k="mail" style={contactIconStyle} /><Editable tag="span" className="st-ed-inline" value={f.email} edit={edit} placeholder="Email" onCommit={(v) => setF({ email: v })} /></span>}
          </div>
        </FreeWrap>
      )}

      {f.note && (
        <FreeWrap id="note" attr={f.noteAttributes} defaultPos={defaults.note} edit={edit} ed={ed} bounds={bounds}>
          <div className="st-foot-meta" style={{ width: '100%', height: '100%', boxSizing: 'border-box', ...getTextStyle(f.noteAttributes) }}>
            <Editable tag="span" className="st-ed-inline" value={f.note} edit={edit} placeholder="System note" onCommit={(v) => setF({ note: v })} />
          </div>
        </FreeWrap>
      )}

      {f.pageNumbers && (
        <FreeWrap id="pageNumbers" attr={{ ...(f.pageNumbersAttributes || {}), ...(f.pageNumbersAttributes?.x === 600 ? { x: defaults.pageNumbers.x } : {}) }} defaultPos={defaults.pageNumbers} edit={edit} ed={ed} bounds={bounds}>
          <div className="st-foot-meta" style={{ width: '100%', height: '100%', boxSizing: 'border-box', justifyContent: 'flex-end', ...getTextStyle(f.pageNumbersAttributes) }}>
            <span className="st-foot-pg" style={{ margin: 0 }}>{fmt}</span>
          </div>
        </FreeWrap>
      )}

      {(m.sections || []).filter((s: any) => s.zone === 'footer').map((sec: any) => (
        <FreeWrap key={sec.id} id={sec.id} attr={{ ...sec.attributes, x: sec.x ?? sec.attributes?.x, y: sec.y ?? sec.attributes?.y, w: sec.width ?? sec.attributes?.w, h: sec.height ?? sec.attributes?.h, width: sec.width ?? sec.attributes?.width, height: sec.height ?? sec.attributes?.height, type: sec.type, content: sec.content }} defaultPos={{ x: 50, y: 50, w: 200, h: 50 }} edit={edit} ed={ed} bounds={bounds} />
      ))}
    </div>
  );
}

function Watermark({ m }) {
  const w = m.watermark;
  if (!w.show) return null;
  const src = m.header?.logoSrc || '';
  return (
    <div className="st-wm" style={{ opacity: w.opacity / 100 }}>
      <img src={src} alt=""
        style={{ width: w.size + '%', transform: `rotate(${w.rotation}deg)` }} />
    </div>
  );
}

// ══ A4 PAGE — stationery wraps the body ═══════════════════════
// mode: 'letterhead' (header+footer shown) | 'preprinted' (reserved blank margins)
function PaperSheet({ m, type, pageIndex, total, sample, ed }) {
  const paper = m.paper || 'A4';
  const orient = m.orient || 'portrait';
  let [pageW, pageH] = paper === 'Letter' ? [816, 1056] : [794, 1123];
  if (orient === 'landscape') { const t = pageW; pageW = pageH; pageH = t; }

  const pre = m.mode === 'preprinted';
  // Ensure that if repeat is missing (older templates), it defaults to 'all'
  const headerRepeat = m.header.repeat || 'all';
  const headerShown = !pre && m.header.show && (headerRepeat === 'all' || pageIndex === 0);

  const compactHead = headerRepeat === 'all' && m.header.compactCont && pageIndex > 0;

  const footerRepeat = m.footer.repeat || 'all';
  const footerShown = !pre && m.footer.show && (footerRepeat === 'all' || pageIndex === 0);
  const compactFoot = false;
  const accent = DOC_META[type].accent;
  // editing is scoped to the master (header/footer/logo) on the first page only
  const edHead = (ed && ed.on && pageIndex === 0 && !compactHead) ? ed : null;
  const edFoot = (ed && ed.on && pageIndex === 0) ? ed : null;

  const innerStyle = pre
    ? { paddingTop: m.reserve.top, paddingBottom: m.reserve.bottom }
    : {};

  return (
    <div
      className="pbd-page"
      data-screen-label={`Page ${pageIndex + 1}`}
      style={{
        ['--pbd-accent']: accent,
        width: `${pageW}px`,
        minWidth: `${pageW}px`,
        maxWidth: `${pageW}px`,
        minHeight: `${pageH}px`,
      }}
    >
      <Watermark m={m} />

      {pre && <div className="st-reserve st-reserve--top" style={{ height: m.reserve.top }}>
        <span><Ic k="panelL" />Reserved · pre-printed letterhead ({m.reserve.top}px)</span>
      </div>}
      {headerShown && <Header m={m} compact={compactHead} ed={edHead} />}

      <div className="pbd pbd-page-inner" data-density="compact" style={{ ['--pbd-accent']: accent, ...innerStyle }}>
        {sample
          ? <Body type={type} pageIndex={pageIndex} total={total} />
          : <div className="st-blank"><span>{ed && ed.on ? 'Document content appears here · edit the header & footer around it' : 'Blank stationery · content area'}</span></div>}
      </div>

      {footerShown && <Footer m={m} pageIndex={pageIndex} total={total} compact={compactFoot} ed={edFoot} />}
      {pre && <div className="st-reserve st-reserve--bot" style={{ height: m.reserve.bottom }}>
        <span><Ic k="panelR" />Reserved · pre-printed footer ({m.reserve.bottom}px)</span>
      </div>}
    </div>
  );
}

export const STDoc = { PaperSheet, DOC_META, Header, Footer, Watermark, Body };


